import React, { useState, useCallback, useEffect } from "react";
import { MotiView, View } from "moti";
import { StyleSheet, ActivityIndicator } from "react-native";

import {
  useGetDegreesBySchoolByQueryQuery,
  useCreateDegreeMutation,
  useUpdateDegreeMutation,
  useDeleteDegreeMutation,
  GetDegreesBySchoolByQueryDocument,
} from "@axio/graphql/generated/graphql";
import { useAppConfig } from "../../AppConfigProvider";
import usePagination from "../../hooks/usePagination";
import useSortingDropdown from "../../hooks/useSortingDropdown";
import AdminLayout from "../../layouts/AdminLayout.web";
import AddDegreeModal from "../../components/adminDashboard/DegreesPrograms/AddDegreeModal";
import ChangeDegreeNameModal from "../../components/adminDashboard/DegreesPrograms/ChangeDegreeNameModal";
import DeleteDegreeModal from "../../components/adminDashboard/DegreesPrograms/DeleteDegreeModal";
import DegreesHeader from "../../components/adminDashboard/DegreesPrograms/DegreesHeader";
import DegreesTable from "../../components/adminDashboard/DegreesPrograms/DegreesTable";
import DegreesMenu from "../../components/adminDashboard/DegreesPrograms/DegreesMenu";
import { DegreesProgramsModalState } from "../../components/adminDashboard/DegreesPrograms/types";
import AdminTableHeader from "../../components/adminDashboard/AdminTable/AdminTableHeader";
import AdminTable from "../../components/adminDashboard/AdminTable/AdminTable";
import AdminTablePagination from "../../components/adminDashboard/AdminTable/AdminTablePagination";
import CustomText from "../../components/common/general/CustomText/CustomText";

const SORT_ITEMS = [
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
  { label: "Date (Newest First)", value: "date_desc" },
  { label: "Date (Oldest First)", value: "date_asc" },
  { label: "Enrollment (High-Low)", value: "enrollment_desc" },
  { label: "Enrollment (Low-High)", value: "enrollment_asc" },
];

function DegreesProgramsView() {
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const [searchQuery, setSearchQuery] = useState("");
  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 5 });
  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const [modalState, setModalState] = useState({
    state: DegreesProgramsModalState.CLOSED,
    degreeId: "",
    degreeName: "",
    enrolledStudentsCount: 0,
  });

  // Fetch degrees data
  const {
    data: degreesData,
    error,
    refetch: degreesRefetch,
    loading: degreesLoading,
  } = useGetDegreesBySchoolByQueryQuery({
    variables: {
      schoolId,
      searchQuery,
      page,
      pageSize,
      sortBy,
    },
    skip: !schoolId,
  });

  const degrees = degreesData?.getDegreesBySchoolByQuery?.data.degrees ?? [];
  const totalCount = degreesData?.getDegreesBySchoolByQuery?.data.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Reset page when search or sort changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  // Refetch data when search parameters change
  useEffect(() => {
    degreesRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  // Refetch queries for mutations
  const refetchQueries = [
    {
      query: GetDegreesBySchoolByQueryDocument,
      variables: {
        schoolId,
        searchQuery,
        page,
        pageSize,
        sortBy,
      },
    },
  ];

  // Mutations
  const [createDegree] = useCreateDegreeMutation();
  const [updateDegree] = useUpdateDegreeMutation();
  const [deleteDegree] = useDeleteDegreeMutation();

  // Event handlers
  const handleAddDegree = () => {
    setModalState({
      state: DegreesProgramsModalState.ADD_DEGREE,
      degreeId: "",
      degreeName: "",
      enrolledStudentsCount: 0,
    });
  };

  const handleSubmitAddDegree = async (formData: { degreeName: string }) => {
    try {
      const result = await createDegree({
        variables: {
          name: formData.degreeName,
          schoolId,
        },
        refetchQueries,
      });
      return result.data;
    } catch (error) {
      console.error("Error creating degree:", error);
      return {
        createDegree: {
          data: null,
          success: false,
        },
      };
    }
  };

  const handleSubmitChangeDegreeName = async (formData: { degreeName: string }) => {
    try {
      const result = await updateDegree({
        variables: {
          id: modalState.degreeId || "",
          degree: {
            name: formData.degreeName,
          },
        },
        refetchQueries,
      });
      return result.data;
    } catch (error) {
      console.error("Error updating degree:", error);
      return {
        updateDegree: {
          data: null,
          success: false,
        },
      };
    }
  };

  const handleSubmitDeleteDegree = async (formData: { confirmation?: "DELETE" }) => {
    try {
      const result = await deleteDegree({
        variables: {
          id: modalState.degreeId || "",
        },
        refetchQueries,
      });
      return result.data;
    } catch (error) {
      console.error("Error deleting degree:", error);
      return {
        deleteDegree: {
          data: null,
          success: false,
        },
      };
    }
  };

  const handleActionModalOpen = (
    degreeId: string,
    degreeName: string,
    enrolledStudentsCount: number,
    state: DegreesProgramsModalState
  ) => {
    setModalState({
      state,
      degreeId,
      degreeName,
      enrolledStudentsCount,
    });
  };

  const handleCloseModal = () => {
    setModalState({
      state: DegreesProgramsModalState.CLOSED,
      degreeId: "",
      degreeName: "",
      enrolledStudentsCount: 0,
    });
  };

  return (
    <AdminLayout>
      <MotiView
        from={{
          opacity: 0,
          translateY: 20,
          scale: 1,
        }}
        animate={{
          opacity: 1,
          translateY: 0,
          scale: 1,
        }}
        delay={10}
        // @ts-expect-error - transition is not typed
        transition={{ type: "timing", duration: 500 }}>
        <DegreesHeader totalCount={totalCount} onAddDegree={handleAddDegree} />

        <AdminTable>
          <AdminTableHeader
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDropdownOpen={sortDropdownOpen}
            setSortDropdownOpen={setSortDropdownOpen}
            sortItems={SORT_ITEMS}
          />
          {degreesLoading ? (
            <View style={styles.loaderContainer}>
              <ActivityIndicator size="large" color="#0084FF" />
            </View>
          ) : degrees.length > 0 ? (
            <DegreesTable degrees={degrees} onActionModalOpen={handleActionModalOpen} />
          ) : (
            <View style={styles.emptyState}>
              <CustomText text="No degree programs found." />
            </View>
          )}
          {totalCount > 0 && (
            <AdminTablePagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onItemsPerPageChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(1);
              }}
            />
          )}
        </AdminTable>

        <AddDegreeModal
          visible={modalState.state === DegreesProgramsModalState.ADD_DEGREE}
          onClose={handleCloseModal}
          onSubmit={handleSubmitAddDegree}
        />

        <ChangeDegreeNameModal
          visible={modalState.state === DegreesProgramsModalState.CHANGE_DEGREE_NAME}
          onClose={handleCloseModal}
          onSubmit={handleSubmitChangeDegreeName}
          currentDegreeName={modalState.degreeName || ""}
        />

        <DeleteDegreeModal
          visible={modalState.state === DegreesProgramsModalState.DELETE_DEGREE}
          onClose={handleCloseModal}
          onSubmit={handleSubmitDeleteDegree}
          degreeName={modalState.degreeName || ""}
          enrolledStudentsCount={modalState.enrolledStudentsCount || 0}
        />
      </MotiView>
    </AdminLayout>
  );
}

export default DegreesProgramsView;

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
});
